<script>
	import { gridToPixels, getImageAspectRatio, pixelsToGridDelta, pixelsToWidthDelta, pixelsToLengthDelta } from './lib/iso.js';
	import { createEventDispatcher } from 'svelte';

	const debug = (typeof window !== 'undefined' && window.DEBUG) ? window.DEBUG : false;

	export let X = 0;  // grid X (tiles)
	export let Y = 0;  // grid Y (tiles)
	export let W = 0;  // grid width (tiles)
	export let L = 0;  // grid length (tiles)
	export let cell = 80; // px
	export let color = '#ff4d4f';
	export let img = '';
	export let status = 'null';
	export let label = '';
	export let id = null;
	export let panX = 0;
	export let panY = 0;
	export let interactive = true;
	export let editable = false; // creative mode: drag-to-move / drag-to-resize
	export let selected = false; // elevated z-index so it's grabbable over overlapping items
	export let dimmed = false; // lowered opacity: sibling of the focused content-detail

	const dispatch = createEventDispatcher();
	const DRAG_THRESHOLD = 3; // px, matches GridView's pan threshold

	let aspectRatio = null;
	let pos = { x: 0, y: 0 , w: 0, h: 0}; // ensure defined before first render

	// Local overrides while a drag/resize is in progress — props are one-way,
	// so the live pixel position during a drag comes from these instead of
	// waiting for the parent to re-render with new X/Y/W/L.
	let dragX = null, dragY = null, dragW = null, dragL = null;
	$: displayX = dragX ?? X;
	$: displayY = dragY ?? Y;
	$: displayW = dragW ?? W;
	$: displayL = dragL ?? L;
	// Drop each override once the parent's prop has caught up, so there's no
	// snap-back flicker once the PATCH's optimistic update lands.
	$: if (dragX !== null && Number(X) === dragX) dragX = null;
	$: if (dragY !== null && Number(Y) === dragY) dragY = null;
	$: if (dragW !== null && Number(W) === dragW) dragW = null;
	$: if (dragL !== null && Number(L) === dragL) dragL = null;

	function round2(v) {
		return Math.round(v * 100) / 100;
	}

	// Load image and get aspect ratio when img is provided
	$: if (img) {
		const imgPath = `${img}`;
		getImageAspectRatio(imgPath)
			.then(ratio => {
				aspectRatio = ratio;
			})
			.catch(err => {
				console.error(err);
				aspectRatio = null;
			});
	} else {
		aspectRatio = null;
	}

	// Calculate position using aspect ratio if available
	$: pos = gridToPixels(displayX, displayY, displayW, displayL, cell, aspectRatio); // returns { x, y, w, h } in CSS pixels
	$: if (debug) console.log(`element ${label} : grid position : (X=${displayX}, Y=${displayY}), grid size : (W=${displayW}, L=${displayL}); screen position : (x=${pos?.x}, y=${pos?.y}), screen size : (w=${pos?.w}, h=${pos?.h})`);

	// Move: pointerdown/move/up on the marker body, gated on `editable` so
	// non-edit-mode click-to-open behavior is untouched.
	let pressed = false;
	let dragging = false;
	let justDragged = false; // suppresses the 'open' click that follows a real drag
	let dragStart = { x: 0, y: 0 };
	let startCoords = { X: 0, Y: 0 };

	function handleBodyPointerDown(e) {
		// Selection is panel-only (see the corner picker in App.svelte) — a
		// direct click/drag on an unselected marker must not move it, so this
		// only arms once the item is already the selected one.
		if (!editable || !selected) return;
		if (e.button !== 0) return;
		pressed = true;
		dragging = false;
		dragStart = { x: e.clientX, y: e.clientY };
		startCoords = { X: Number(X), Y: Number(Y) };
		e.currentTarget.setPointerCapture?.(e.pointerId);
		e.stopPropagation();
	}

	function handleBodyPointerMove(e) {
		if (!pressed) return;
		const dxPx = e.clientX - dragStart.x;
		const dyPx = e.clientY - dragStart.y;
		if (!dragging) {
			if (Math.hypot(dxPx, dyPx) < DRAG_THRESHOLD) return;
			dragging = true;
		}
		const { dX, dY } = pixelsToGridDelta(dxPx, dyPx, cell);
		dragX = startCoords.X + dX;
		dragY = startCoords.Y + dY;
	}

	function handleBodyPointerUp(e) {
		if (!pressed) return;
		pressed = false;
		try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
		if (dragging) {
			justDragged = true;
			dispatch('reposition', { key: id, X: round2(dragX), Y: round2(dragY), W: Number(W), L: Number(L) });
		}
		dragging = false;
	}

	// Resize: two independent edge handles, one per grid axis (see iso.js —
	// W only shifts the left edge, L only shifts the right edge).
	let resizing = null; // 'W' | 'L' | null
	let resizeStartX = 0;
	let startWL = { W: 0, L: 0 };

	function startResize(axis, e) {
		if (e.button !== 0) return;
		resizing = axis;
		resizeStartX = e.clientX;
		startWL = { W: Number(W), L: Number(L) };
		e.currentTarget.setPointerCapture?.(e.pointerId);
		e.stopPropagation();
		e.preventDefault();
	}

	function moveResize(e) {
		if (!resizing) return;
		const dxPx = e.clientX - resizeStartX;
		if (resizing === 'W') {
			dragW = Math.max(0.1, startWL.W + pixelsToWidthDelta(dxPx, cell));
		} else {
			dragL = Math.max(0.1, startWL.L + pixelsToLengthDelta(dxPx, cell));
		}
	}

	function endResize(e) {
		if (!resizing) return;
		resizing = null;
		try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
		dispatch('resize', { key: id, X: Number(X), Y: Number(Y), W: round2(dragW ?? W), L: round2(dragL ?? L) });
	}

	function handleClick() {
		if (justDragged) { justDragged = false; return; }
		if (interactive) dispatch('open', { id });
	}
	function handleKeydown(e) {
		if (interactive && (e.key === 'Enter' || e.key === ' ')) dispatch('open', { id });
	}

	// Map status to glow color
	$: glowColor = (() => {
		switch(status) {
			case 'all':
				return 'rgba(0, 255, 0, 0.8)';
			case 'patched':
				return 'rgba(0, 255, 255, 0.8)';
			case 'pwned':
				return 'rgba(255, 0, 0, 0.8)';
			case 'null':
			default:
				return 'rgba(200, 255, 200, 0.8)';
		}
	})();
	$: glowColorSecondary  = glowColor.replace('0.8', '0.4');
	$: glowColorRest       = glowColor.replace('0.8', '0.3');
	$: glowColorRestOuter  = glowColor.replace('0.8', '0.1');
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -- role/tabindex are only ever both set together (interactive=true) or both absent; the linter can't see that from the dynamic expressions below. -->
<div class="grid-object" class:interactive class:editable class:selected class:dragging class:dimmed id={id}
	role={interactive ? 'button' : undefined}
	tabindex={interactive ? 0 : undefined}
	aria-label={interactive ? label : undefined}
	title={interactive ? label : undefined}
	style="left:{pos.x + panX}px; top:{pos.y + panY}px; --glow-color: {glowColor}; --glow-color-secondary: {glowColorSecondary}; --glow-rest: {glowColorRest}; --glow-rest-outer: {glowColorRestOuter};"
	on:pointerdown={handleBodyPointerDown}
	on:pointermove={handleBodyPointerMove}
	on:pointerup={handleBodyPointerUp}
	on:pointercancel={handleBodyPointerUp}
	on:click={handleClick}
	on:keydown={handleKeydown}>
	{#if img}
	<span class="object" style="background-image:url({img}); width:{pos.w}px; height:{pos.h}px;"></span>
	{:else}
	<span class="object" style="background-color:{color}; width:{pos.w}px; height:{pos.h}px;"></span>
	{/if}
	{#if editable && selected}
	<span class="resize-handle handle-w" title="Largeur (W)"
		style="left:{-6}px; top:{(pos.h / 2) - 6}px"
		on:pointerdown={(e) => startResize('W', e)}
		on:pointermove={moveResize}
		on:pointerup={endResize}
		on:pointercancel={endResize}></span>
	<span class="resize-handle handle-l" title="Longueur (L)"
		style="left:{pos.w - 6}px; top:{(pos.h / 2) - 6}px"
		on:pointerdown={(e) => startResize('L', e)}
		on:pointermove={moveResize}
		on:pointerup={endResize}
		on:pointercancel={endResize}></span>
	{/if}
	<slot />
</div>

<style>
	.grid-object {
		position: absolute;
		transform: translate(-50%, 0); /* anchor at top-center */
		display: inline-flex;
		flex-direction: column;
		align-items: center;
		gap: 6px;
		pointer-events: none;
		cursor: default;
		transition: opacity 150ms;
	}

	.grid-object.dimmed {
		opacity: 0.35;
	}

	.grid-object.interactive {
		pointer-events: auto;
		cursor: pointer;
	}

	/* Only the selected marker is grabbable in edit mode — selection itself
	   only ever comes from the corner picker, never from clicking the grid. */
	.grid-object.editable.selected {
		pointer-events: auto;
		cursor: grab;
	}
	.grid-object.editable.selected.dragging {
		cursor: grabbing;
	}
	.grid-object.selected {
		z-index: 50;
	}

	.resize-handle {
		position: absolute;
		width: 12px;
		height: 12px;
		border-radius: 3px;
		background: #ff4d4f;
		border: 1px solid rgba(0, 0, 0, 0.6);
		cursor: ew-resize;
		pointer-events: auto;
		z-index: 2;
	}

	.object {
		position: absolute;
		left: 50%;
		top: 0;
		width: 100%;
		height: 100%;
		pointer-events: inherit;
		background-repeat: no-repeat;
		background-size: 100% auto;
		filter: drop-shadow(0 0 3px var(--glow-rest)) drop-shadow(0 0 8px var(--glow-rest-outer));
		transition: filter 0.3s ease;
	}

	.grid-object.interactive .object:hover {
		filter: drop-shadow(0 0 12px var(--glow-color)) drop-shadow(0 0 24px var(--glow-color-secondary));
	}
</style>


